import React, { useLayoutEffect, useRef } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themesAnimated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themesAnimated);

// helpers
const startOfWeek = (date) => {
  // returns Monday as week start (modify if you want Sunday)
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sun, 1 = Mon, ...
  const diff = (day + 6) % 7; // days since Monday
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const startOfMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

const aggregateByScale = (rawData = [], scale = "daily") => {
  if (!Array.isArray(rawData) || rawData.length === 0) return [];

  // DAILY: preserve volatile/quarantined per item
  if (scale === "daily") {
    return rawData
      .map((it) => ({
        date: new Date(it.period),
        total: Number(it.total || 0),
        volatile: Boolean(it.volatile),
        quarantined: Boolean(it.quarantined),
      }))
      .sort((a, b) => a.date - b.date);
  }

  const map = new Map();

  // WEEKLY: aggregate totals; volatile/quarantined = true if any item in week has it
  if (scale === "weekly") {
    rawData.forEach((it) => {
      const d = new Date(it.period);
      const wk = startOfWeek(d);
      const key = wk.toISOString().slice(0, 10); // YYYY-MM-DD of week start
      const prev = map.get(key);
      const total = (prev ? prev.total : 0) + Number(it.total || 0);
      const volatile = (prev ? prev.volatile : false) || Boolean(it.volatile);
      const quarantined =
        (prev ? prev.quarantined : false) || Boolean(it.quarantined);
      map.set(key, { total, volatile, quarantined });
    });

    return Array.from(map.entries())
      .map(([k, v]) => ({
        date: new Date(k),
        total: v.total,
        volatile: v.volatile,
        quarantined: v.quarantined,
      }))
      .sort((a, b) => a.date - b.date);
  }

  // MONTHLY: same logic as weekly but grouped by month start
  if (scale === "monthly") {
    rawData.forEach((it) => {
      const d = new Date(it.period);
      const m = startOfMonth(d);
      const key = m.toISOString().slice(0, 10); // YYYY-MM-DD of month start
      const prev = map.get(key);
      const total = (prev ? prev.total : 0) + Number(it.total || 0);
      const volatile = (prev ? prev.volatile : false) || Boolean(it.volatile);
      const quarantined =
        (prev ? prev.quarantined : false) || Boolean(it.quarantined);
      map.set(key, { total, volatile, quarantined });
    });

    return Array.from(map.entries())
      .map(([k, v]) => ({
        date: new Date(k),
        total: v.total,
        volatile: v.volatile,
        quarantined: v.quarantined,
      }))
      .sort((a, b) => a.date - b.date);
  }

  // fallback: preserve flags
  return rawData.map((it) => ({
    date: new Date(it.period),
    total: Number(it.total || 0),
    volatile: Boolean(it.volatile),
    quarantined: Boolean(it.quarantined),
  }));
};

const ServiceTypeChart = ({
  data,
  serviceTypeName,
  scale = "daily", // 'daily' | 'weekly' | 'monthly'
  color = "#ffce00",
}) => {
  const chartRef = useRef(null);

  useLayoutEffect(() => {
    const chart = am4core.create(chartRef.current, am4charts.XYChart);

    // aggregate according to scale
    const processed = aggregateByScale(data, scale);

    // attach service name + required fields
    chart.data = processed.map((r) => ({
      date: r.date,
      total: r.total,
      service: serviceTypeName,
      volatile: r.volatile,
      quarantined: r.quarantined,
    }));

    // X Axis (remove vertical grid lines only)
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.grid.template.disabled = true; // hides vertical grid lines

    // baseInterval + date format
    let baseInterval = { timeUnit: "day", count: 1 };
    let dateFormat = "MMM-dd";

    if (scale === "weekly") {
      baseInterval = { timeUnit: "week", count: 1 };
      dateFormat = "MMM dd"; // e.g. Sep 02
    } else if (scale === "monthly") {
      baseInterval = { timeUnit: "month", count: 1 };
      dateFormat = "MMM"; // e.g. Sep
    }

    dateAxis.baseInterval = baseInterval;
    dateAxis.dateFormats.setKey("day", dateFormat);
    dateAxis.dateFormats.setKey("week", dateFormat);
    dateAxis.dateFormats.setKey("month", dateFormat);
    dateAxis.periodChangeDateFormats.setKey("day", dateFormat);
    dateAxis.periodChangeDateFormats.setKey("week", dateFormat);
    dateAxis.periodChangeDateFormats.setKey("month", dateFormat);

    if (scale === "monthly") {
      // default label style
      dateAxis.renderer.labels.template.fontWeight = "normal";

      dateAxis.renderer.labels.template.adapter.add(
        "fontWeight",
        // eslint-disable-next-line func-names
        function (weight, target) {
          const date = target.dataItem?.date;
          if (date instanceof Date) {
            const month = date.toLocaleString("en-US", { month: "short" });
            if (month === "Jan") {
              return "bold"; // only January bold
            }
          }
          return weight;
        }
      );

      // format the label text
      dateAxis.renderer.labels.template.adapter.add(
        "text",
        // eslint-disable-next-line func-names
        function (text, target) {
          const date = target.dataItem?.date;
          if (date instanceof Date) {
            const month = date.toLocaleString("en-US", { month: "short" });

            // ✅ show 2-digit year only for January
            if (month === "Jan") {
              const year = String(date.getFullYear()).slice(-2);
              return `${month} ${year}`; // e.g. Jan 25
            }

            return month; // other months normal
          }
          return text;
        }
      );
    }

    // Y Axis (keep horizontal grid lines)
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.min = 0;

    // Column series
    const series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.dateX = "date";
    series.dataFields.valueY = "total";
    series.tooltipText = `[bold]{service}[/]\n{dateX.formatDate('${dateFormat}')} : {valueY}`;
    // Disable tooltip if volatile
    series.adapter.add("tooltipText", (text, target) => {
      const dataItem = target.tooltipDataItem?.dataContext;
      if (dataItem?.volatile) return ""; // empty disables tooltip
      return text;
    });
    series.columns.template.strokeWidth = 0;
    series.columns.template.fill = am4core.color(color);
    series.columns.template.adapter.add("fill", (fill, target) => {
      const dataItem = target.dataItem?.dataContext;
      if (dataItem?.volatile) {
        return am4core.color("#dddddd"); // gray for volatile
      }
      return am4core.color(color);
    });

    // Cursor
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.xAxis = dateAxis;
    chart.cursor.lineX.disabled = true;
    chart.cursor.lineY.disabled = true;
    chart.cursor.behavior = "none";
    chart.logo.disabled = true;

    return () => {
      chart.dispose();
    };
  }, [data, serviceTypeName, scale, color]);

  return <div ref={chartRef} style={{ width: "100%", height: "400px" }} />;
};

export default ServiceTypeChart;
